<script setup lang="ts">
import { FlyoutWithOverlay, InfiniteListView } from '@/components/shared'
import useGlobalStore from '@/stores/modules/global'
import usePaginationStore from '@/stores/modules/pagination'
import CONSTANTS from '@/utils/constants/string-constants'
import {
  DATE_TIME_FORMAT_12_HRS,
  INSPECTIONS_URL,
  ISSUES_URL,
  JOBS_URL,
  NOTIFICATIONS_LIST_STORE,
  SERVICES_URL,
  WORK_ORDERS_URL
} from '@/utils/constants'
import useJobsStore from '@/stores/modules/jobs'
import { JobType } from '@/enum'
import { JobIconType } from '@/enum/JobType'
import { getLocalDateTime } from '@/utils/helpers'

const globalStore = useGlobalStore()
const listStore = usePaginationStore(NOTIFICATIONS_LIST_STORE)
const jobsStore = useJobsStore()

const getJobTypeURLPrefix = (jobType: JobType) => {
  switch (jobType) {
    case JobType.INSPECTION:
      return INSPECTIONS_URL
    case JobType.ISSUE:
      return ISSUES_URL
    case JobType.MAINTENANCE:
      return SERVICES_URL
    case JobType.WORK_ORDER:
      return WORK_ORDERS_URL
    case JobType.GENERAL:
    default:
      return JOBS_URL
  }
}

const getNotificationIcon = (type: number) => {
  return JobIconType[JobType[type] as keyof typeof JobIconType]
}

const getNotificationIconColor = (type: number) => {
  switch (type) {
    case 1:
      return 'emerald_700'
    case 2:
      return 'blue_700'
    case 3:
      return 'pink_600'
    default:
      return 'pink_600'
  }
}

const jobDetailsNavigationHandler = () => {
  globalStore.showNotifications = false
}
</script>
<template>
  <FlyoutWithOverlay v-model="globalStore.showNotifications" flyout-class="d-flex flex-column">
    <template #flyout-header>
      <h6 class="heading-3 semibold">{{ CONSTANTS.NOTIFICATIONS }}</h6>
    </template>
    <div class="d-flex flex-column flex-grow-1 overflow-y--hidden">
      <div
        v-if="listStore.noData"
        class="d-flex flex-column ga-3 align-center justify-center no-notifications-wrapper"
      >
        <span class="bg-blue_50 notification--icon-wrapper">
          <v-icon icon="mdi-bell" size="18" color="blue_500" />
        </span>
        <span class="body-1--new">{{ CONSTANTS.NO_NOTIFICATIONS }}</span>
      </div>
      <InfiniteListView
        module-name="notifications"
        reset-on-unmount
        :list-name="NOTIFICATIONS_LIST_STORE"
        :list-item-height="100"
        @api-call="jobsStore.getJobsNotifications"
      >
        <!-- <template #list-header>
          <span class="body-3 text-uppercase text-gray_400 px-4 d-block">{{
            CONSTANTS.OLDER_NOTIFICATIONS
          }}</span>
        </template> -->
        <template #list-item="{ listItem }">
          <router-link
            class="d-flex text-decoration-none inherit--text ga-3 align-start px-3 py-4"
            :to="`${getJobTypeURLPrefix(listItem.job?.type)}/${listItem.jobId}`"
            @click="jobDetailsNavigationHandler"
          >
            <span class="notification--icon-wrapper" :type="listItem.jobType">
              <v-icon
                :icon="getNotificationIcon(listItem.jobType)"
                :color="getNotificationIconColor(listItem.jobType)"
                size="18"
              />
            </span>
            <div class="d-flex flex-column ga-1">
              <span class="mb-1 medium body-2">
                <span>{{ listItem.description }}</span>
              </span>
              <span class="body-3">
                {{ getLocalDateTime(listItem.createdAt, DATE_TIME_FORMAT_12_HRS) }}
              </span>
            </div>
          </router-link>
        </template>
        <template #list-skeleton-loader>
          <div v-for="(_, index) in 10" :key="index" class="d-flex ga-3 align-start px-3 py-4">
            <v-skeleton-loader width="32" height="32" />
            <div class="d-flex flex-column flex-grow-1 ga-1">
              <v-skeleton-loader class="body-1--new mb-1" height="20" width="80%" />
              <v-skeleton-loader class="body-1--new mb-1" height="20" width="30%" />
            </div>
          </div>
        </template>
        <template #list-load-more-loader>
          <div class="d-flex ga-3 align-start px-3 py-4">
            <v-skeleton-loader width="32" height="32" />
            <div class="d-flex flex-column flex-grow-1 ga-1">
              <v-skeleton-loader class="body-1--new mb-1" height="20" width="80%" />
              <v-skeleton-loader class="body-1--new mb-1" height="20" width="30%" />
            </div>
          </div>
        </template>
      </InfiniteListView>
    </div>
  </FlyoutWithOverlay>
</template>
<style scoped lang="scss">
.circular-loader {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.no-notifications-wrapper {
  width: 100%;
  min-height: 180px;
  .notification--icon-wrapper {
    padding: 4px 8px;
    border-radius: 2px;
  }
}
.notification--icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;

  &[type='1'] {
    background-color: rgb(var(--v-theme-emerald_50));
  }
  &[type='2'] {
    background-color: rgb(var(--v-theme-blue_50));
  }
  &[type='3'] {
    background-color: rgb(var(--v-theme-pink_50));
  }
}
</style>
